import React, { useState, useCallback, useEffect, useRef, memo, useMemo } from 'react';
import { format, addDays, subDays, addMinutes, differenceInMinutes, differenceInDays, set, startOfDay, isSameDay } from 'date-fns';
import { motion, AnimatePresence, useMotionValue, useSpring } from 'framer-motion';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CalendarPlus, ChevronLeft, ChevronRight, Clock, Crown, Paperclip, Plus, RefreshCw, Settings, Settings2, SquarePen } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import PlannerHeaderFilter, { HeaderActiveFilters, statusLabels } from './Filter';
import { useAllAssignmentTypes } from '@/lib/useAllAssignmentTypes';
//import { formatTime, formatTimeCompact } from '@/lib/utils';

import { formatTime, formatTimeCompact } from '@/lib/utils';

import { useCourses } from '@/lib/courses';
import { CalendarColumn, CalendarColumnHeader, CalendarRowLines, CalendarTimeIndicators, NonCalendarDragPreview } from './Calendar';
import { PlannerDateChanger } from './AssignmentsPage';
import { MiniCalSettings } from './Settings';
import { usePlanner } from '../contexts/PlannerContext';
import { IncompleteSvg, DoneSvg, ProgressSvg, LowPrioritySvg, MediumPrioritySvg, HighPrioritySvg, GoogleSvg, MicrosoftSvg } from '@/lib/svg';
import { useCalendarEvents } from '../../../../hooks/useCalendarEvents.ts';
import { UNRESTRICTED_LEARNPLUS_TRIAL } from '@/lib/restrictions';
import { useCommunication } from '@/lib/CommunicationWrapper';
import { AssignmentPlanned, AssignmentWithOriginals, defaultTaskValues } from '@/lib/types';
import { useAuth } from '@/lib/auth.ts';
import Color from "color";

import { CalendarSyncButton } from '../calsync/CalendarSyncButton.tsx';
import { TrialStartContent } from '../../trial-start/index.tsx';
import { ClaimTrialContent } from '../../trial-claim/index.tsx';
import { SyncButton } from './SyncButton.tsx';



/*
function autoScheduleTasks(unplanned: any[], items: any[], settings: any = {}) {
    const { offset = 1, timing = "morning" } = settings;
    const timingPreferences: { [key: string]: number } = {
        "morning": 9,    // 9 AM
        "midday": 12,   // 12 PM
        "evening": 18,   // 6 PM  
        "night": 22,     // 10 PM
        "all-day": 9     // Default to 9 AM
    };

    const output = [...items];

    const taskAllocationPerDay: any = {};
    const today = new Date();
    unplanned.forEach((task) => {
        const taskDate = new Date(task.time);
        const diff = Math.max(0, differenceInDays(taskDate, today) - offset);

        taskAllocationPerDay[diff] = {
            "total": (taskAllocationPerDay[diff]?.["total"] || 0) + 1,
            "duration": [...(taskAllocationPerDay[diff]?.["duration"] || []), task.duration]
        }
    });

    let startIndex = 0;
    Object.keys(taskAllocationPerDay).forEach((elaspedDays) => {

        const recommendedTime = timingPreferences[timing];
        const currentTime = new Date(today.getFullYear(), today.getMonth(), today.getDate() + parseInt(elaspedDays), recommendedTime, 0);
        for (let i = 0; i < taskAllocationPerDay[elaspedDays]["total"]; i++) {
            const index = startIndex + i;
            const task = unplanned[index];
            console.log("task", task.title);
            //const time = findClosestAvailableTime(task.id, currentTime);
            const time = currentTime;
            if (!time) {
                console.error("no time could be scheduled for task", task.title);
                return;
            }xr
            output[index] = {
                ...task,
                plannedAt: new Date(time).toISOString(),
            }
            currentTime.setMinutes(currentTime.getMinutes() + task.duration + 15);
        }
        startIndex += taskAllocationPerDay[elaspedDays]["total"];
    });

    return output;
}

*/


const areSameDay = (date1: Date, date2: Date) => {
    return date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
}

const StatusBubble = ({ item }: { item: AssignmentWithOriginals }) => {

    const status = item?.current?.status;

    const { modifyOverrides } = usePlanner();


    const handleStatusChange = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        if (status === "complete" || status === "graded") {
            modifyOverrides(item.current.id, { "status": "incomplete" });
        } else if (status === "incomplete") {
            modifyOverrides(item.current.id, { "status": "in-progress" });
        } else if (status === "in-progress") {
            modifyOverrides(item.current.id, { "status": "complete" });
        }
    }

    const styles = {
        "filled": "bg-theme-link text-theme-bg",
        "outline": "bg-theme-0 text-theme-link"
    }

    const Icon = status === "in-progress" ? ProgressSvg : status === "complete" || status === "graded" ? DoneSvg : IncompleteSvg;

    return (
        <button className={`min-w-max cursor-pointer opacity-75 hover:scale-105 hover:opacity-100 transition-all duration-200 flex items-center gap-[5px] rounded-md py-[1px]`} onClick={handleStatusChange}>
            <Icon className="size-5 text-theme-link" />
        </button>
    )

    return (
        <button className={`min-w-max cursor-pointer hover:scale-102 transition-all duration-200 flex items-center gap-[5px] rounded-md px-[6px] py-[1px] ${status === "in-progress" ? "border-[2px]" : "border"} border-theme-link ${status === "complete" || status === "graded" ? styles["filled"] : styles["outline"]}`} onClick={handleStatusChange}>
            <p className="text-sm font-medium">{statusLabels[status]}</p>
        </button>
    )
}

const PriorityBubble = ({ priority }: { priority: number | null }) => {

    if (!priority) return null;

    const Icon = priority === 1 ? LowPrioritySvg : priority === 2 ? MediumPrioritySvg : HighPrioritySvg;
    const text = priority === 1 ? "Low" : priority === 2 ? "Medium" : priority === 3 ? "High" : "";

    return (
        <button className="rounded-md px-[5px] py-[2px] bg-theme-1 flex items-center gap-1">
            <Icon className="size-3.5 fill-text-theme-0" />
            <p className="text-xs text-theme-1">{text}</p>
        </button>
    )
}

export const DueItemContent = ({ item, onAutoSchedule, onEdit }: { item: AssignmentWithOriginals, onAutoSchedule: (e: React.MouseEvent<HTMLButtonElement>) => void, onEdit: () => void }) => {

    const { settings } = usePlanner();
    const assignmentUrl = item?.current?.url || item?.original?.url;

    const display = settings?.assignments_display_off || [];

    const { courseData } = useCourses();
    const { typeLabels: typeLabelsMap } = useAllAssignmentTypes();

    const unassigned = {
        "default": "No course",
        "code": "",
        "color": "gray"
    }

    const course = courseData?.[item?.current?.course || ""] || unassigned;

    // todo: move this into context and use an object instead of an array of settings
    const itemIsPlanned = item?.current?.plannedAt !== null;
    const showEstimatedTime = !settings?.assignments_display_off?.includes("estimated");
    const showPlannedTime = !settings?.assignments_display_off?.includes("planned");
    const showLinkedItems = !settings?.assignments_display_off?.includes("linked");
    const showPriority = !settings?.assignments_display_off?.includes("priority");
    const showType = !settings?.assignments_display_off?.includes("type");
    const showPoints = !settings?.assignments_display_off?.includes("points");
    const assignmentTitle = item?.current?.title || "Untitled";

    return (
        <>
            <div className={`relative z-[1] bg-theme-0 rounded-xl`}>
                <div className="border-l-[6px] rounded-xl p-[15px]" style={{ borderColor: course.color }}>
                    <div className="flex items-center justify-between gap-[5px]">
                        <div className="flex items-center gap-[5px] min-w-0">
                            <div className="h-[10px] w-[10px] rounded-full" style={{ "background": course.color }} />
                            <p className={`text-sm truncate ${item?.current?.course != item?.original?.course ? "text-theme-link" : "text-theme-1"}`}>{course.code || course.default}</p>
                        </div>
                        <StatusBubble item={item} />
                    </div>
                    <div className="mt-[5px] max-w-full">
                        <div className="flex items-center gap-0 max-w-full min-w-0">
                            <a
                                className={`group text-base font-medium text-left inline-flex items-center gap-0 max-w-full min-w-0 overflow-hidden hover:underline ${item?.current?.status === "complete" || item?.current?.status === "graded" ? "line-through" : item?.current?.title !== item?.original?.title ? "text-theme-link" : "text-theme-0"}`}
                                href={assignmentUrl || undefined}
                                target={assignmentUrl ? "_blank" : undefined}
                                rel={assignmentUrl ? "noreferrer" : undefined}
                                onClick={(e) => {
                                    if (!assignmentUrl) return;
                                    e.stopPropagation();
                                }}
                            >
                                <span className="truncate min-w-0">{assignmentTitle}</span>
                            </a>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        className="flex-shrink-0 h-7 w-7 rounded-md text-theme-1 hover:bg-theme-1 hover:text-theme-0 transition-colors flex items-center justify-center"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit();
                                        }}
                                        aria-label="Edit assignment"
                                    >
                                        <SquarePen className="h-[14px] w-[14px]" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent className="text-xs">
                                    <span>Edit assignment</span>
                                </TooltipContent>
                            </Tooltip>

                        </div>

                    </div>
                    <div className="flex items-center gap-[5px] text-xs mt-[5px]">
                        {showPriority && <PriorityBubble priority={item?.current?.priority} />}
                        {showType && <p className={`rounded-md px-[5px] py-[2px] bg-theme-1 ${item?.current?.type !== item?.original?.type ? "text-theme-link" : "text-theme-1"}`}>{typeLabelsMap[item?.current?.type] || "Other"}</p>}
                        {showPoints && (item?.current?.points || item?.current?.points === 0) && <p className={`rounded-md px-[5px] py-[2px] bg-theme-1 ${item?.current?.points !== item?.original?.points ? "text-theme-link" : "text-theme-1"}`}>{(item?.current?.score !== null ? item?.current?.score + "/" : "") + item?.current?.points}pts</p>}
                    </div>
                </div>
            </div>
            <div className="relative z-[1] px-[10px] py-[5px] flex justify-between">
                <div className="flex items-center gap-[15px] text-theme-1">
                    {!itemIsPlanned && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onAutoSchedule(e);
                            }}
                            className="flex items-center gap-[5px] hover:bg-theme-1 rounded-md px-[5px] py-[2px] cursor-pointer text-theme-link"
                        >
                            <CalendarPlus className="h-[16px] w-[16px]" />
                            <p className="text-sm font-semibold">Auto Plan</p>
                        </button>
                    )}
                    {itemIsPlanned && showEstimatedTime && (
                        <div className={`flex items-center gap-[5px] hover:bg-theme-1 rounded-md ${item?.current?.duration !== item?.original?.duration ? "text-theme-link" : "text-theme-1"}`}>
                            <Clock className="h-[16px] w-[16px] text-inherit" />
                            <p className={`text-sm`}>{formatDurationShort(item?.current?.duration || defaultTaskValues.duration)}</p>
                        </div>
                    )}
                    {itemIsPlanned && showPlannedTime && <p className={`text-sm ${item?.current?.plannedAt !== item?.original?.plannedAt ? "text-theme-link" : "text-theme-1"}`}>@ {formatTimeCompact(item?.current?.plannedAt)}</p>}
                    {showLinkedItems && ((item?.current as any)?.numLinks || 0) > 0 && (
                        <div className="flex items-center gap-[5px]">
                            <Paperclip className="h-[16px] w-[16px]" />
                            <p className="text-sm">{(item?.current as any)?.numLinks || 0}</p>
                        </div>
                    )}
                </div>
                <p className={`text-sm rounded-md px-[5px] py-[2px] font-semibold ${item?.current?.due !== item?.original?.due ? "text-theme-link" : "text-theme-1"}`}>Due {item?.current?.due ? formatDueShort(item?.current?.due) : "No due date"}</p>
            </div>
        </>
    )
}

const DueItemContainer = ({ item }: { item: any }) => {

    const { autoScheduleTasks, setCreatorAssignment } = usePlanner();

    const { checkGate, loadSharedComponent } = useCommunication();


    const handleAutoScheduleItem = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        const response = await checkGate("assignments_auto_schedule");
        if (response.restricted) return;

        autoScheduleTasks([item]);
    }, [item, autoScheduleTasks]);



    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: "due_item_" + item?.current?.id,
        data: {
            "type": "due-item",
            "planner": {
                "id": item?.current?.id,
                "title": item?.current?.title,
                "course": item?.current?.course,
                "duration": item?.current?.duration || defaultTaskValues.duration,
                "plannedAt": item?.current?.plannedAt ?? null,
            }
        }
    });

    return (
        <div
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            className={`flex flex-col w-full rounded-[15px] p-[1px] pl-[0px] relative overflow-hidden cursor-grab hover:brightness-95 transition-all duration-200 ${item?.current?.status === "complete" || item?.current?.status === "graded" ? "opacity-50" : ""} ${isDragging ? 'cursor-move opacity-50' : ''}`}
            onClick={() => {
                const assignmentUrl = item?.current?.url || item?.original?.url;
                if (assignmentUrl) {
                    window.open(assignmentUrl, "_blank", "noopener,noreferrer");
                } else {
                    loadSharedComponent("AssignmentCreator", { assignment: item });
                }
            }}
        >
            <DueItemContent
                item={item}
                onAutoSchedule={handleAutoScheduleItem}
                onEdit={() => loadSharedComponent("AssignmentCreator", { assignment: item })}
            />
            <div className={`absolute top-0 right-0 h-full w-full z-0 bg-theme-link opacity-[.22]`} />
        </div>

    );
}

const DueColumn = ({ date, items, isBlurred }: { date: Date, items: any[], isBlurred?: boolean }) => {

    const renderItems = useMemo(() => {
        return items.map((item) => {
            return <DueItemContainer key={item.current.id} item={item} />
        })
    }, [items]);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
        <div className={`w-[var(--due-block-width)] flex flex-col gap-[10px] relative ${isBlurred ? 'pointer-events-none' : ''}`}>
            <DueColumnHeader date={date} diff={differenceInDays(date, today)} />
            <DueColumnAddItem date={date} />
            <div className={`flex flex-col gap-[10px] relative ${isBlurred ? 'blur-xs pointer-events-none' : ''}`}>
                {renderItems}
            </div>
        </div>
    )
}


const DueColumnAddItem = ({ date }: { date: Date }) => {

    const { loadSharedComponent } = useCommunication();

    return (
        <button className="hover:bg-theme-1 transition-background duration-200 w-full border-theme font-medium border flex items-center justify-center gap-[10px] rounded-xl px-[10px] py-[5px] cursor-pointer" onClick={() => {
            const dueDate = new Date(date);
            dueDate.setHours(23, 59, 0, 0);
            loadSharedComponent("AssignmentCreator", { initialDate: dueDate });
        }}>
            <Plus className="h-[16px] w-[16px]" />
            <p>Add Task</p>
        </button>
    )

}

const DueColumns = ({ offset, isRestrictedUser }: { offset: number, isRestrictedUser: boolean }) => {

    const { startDate, dueItems } = usePlanner();

    console.log("[DueColumns] dueItems", dueItems);

    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    const twoWeeksFromToday = useMemo(() => addDays(today, 14), [today]);

    return useMemo(() => {
        return Array.from({ length: 7 }, (_, i) => {
            const columnDate = addDays(startDate, i + offset);
            columnDate.setHours(0, 0, 0, 0); // Normalize for comparison
            const items = dueItems.filter((item: any) => {
                const current = item.current;
                const due = new Date(current.due);
                return due.getMonth() === columnDate.getMonth() && due.getDate() === columnDate.getDate();
            });
            // Check if this column is 14+ days from today
            const isBlurred = isRestrictedUser && columnDate >= twoWeeksFromToday;
            return <DueColumn key={columnDate.toISOString()} date={columnDate} items={items} isBlurred={isBlurred} />;
        });
    }, [startDate, dueItems, offset, isRestrictedUser, twoWeeksFromToday]);
}

const HeaderDisplayItem = memo(({ label, onClick, checked }: { label: string, onClick: () => void, checked: boolean }) => {
    return (
        <button onClick={onClick} className={`text-sm rounded-full px-[10px] py-[2px] transition-all duration-200 hover:bg-theme-1 ${checked ? "bg-theme-1" : "text-theme-1"}`}>
            <p>{label}</p>
        </button>
    )
})

const displayLabels: any = {
    "type": "Assignment type",
    "points": "Points",
    "estimated": "Estimated time",
    "planned": "Planned at",
    "linked": "Linked items",
}

const HeaderDisplayContent = () => {
    const { settings, updateSettings } = usePlanner();

    const handleToggle = (item: any) => {
        if (settings?.assignments_display_off?.includes(item)) {
            updateSettings({ "assignments_display_off": settings?.assignments_display_off?.filter((i: any) => i !== item) });
        } else {
            updateSettings({ "assignments_display_off": [...(settings?.assignments_display_off || []), item] });
        }
    }

    const render = () => {
        return Object.keys(displayLabels).map((item: any) => {
            return (
                <HeaderDisplayItem key={item} label={displayLabels[item]} checked={!settings?.assignments_display_off?.includes(item)} onClick={() => handleToggle(item)} />
            )
        })
    }

    return (
        <div className="flex items-center gap-[5px] flex-wrap">
            {render()}
        </div>
    )
}

const HeaderDisplay = () => {

    const { portal } = usePlanner();
    const [open, setOpen] = useState(false);
    const lastToggleTime = useRef(0);

    const handleOpenChange = (newOpen: boolean) => {
        const now = Date.now();
        const timeSinceLastToggle = now - lastToggleTime.current;

        if (timeSinceLastToggle < 100) {
            return;
        }

        lastToggleTime.current = now;
        setOpen(newOpen);
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <button className="text-sm flex items-center gap-[5px] px-[10px] rounded-lg py-1 text-theme-0 border border-theme cursor-pointer hover:bg-theme-1 transition-all duration-200">
                    <Settings2 className="h-[16px] w-[16px]" />
                    <span className="font-medium">Display</span>
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[240px] p-[10px]">
                <p className="text-sm font-semibold mb-[5px]">Display properties</p>
                <HeaderDisplayContent />
            </PopoverContent>
        </Popover>
    )
}


const DueColumnsHeader = () => {
    const { loadSharedComponent, useRerenderListener } = useCommunication();
    const { options } = useRerenderListener(["google_calendars"]);
    const calendarConnected = Object.keys(options?.google_calendars || {}).length > 0;

    return (
        <div className="flex flex-col border-b border-theme">
            <div className="h-[50px] px-4 flex items-center gap-4">
                <PlannerDateChanger />
                <div className="flex items-center gap-2">
                    <PlannerHeaderFilter />
                    <HeaderDisplay />
                    <SyncButton />
                </div>
            </div>
            <HeaderActiveFilters />
        </div>
    )
}

const DueColumnsContainer = () => {

    const droppable = useDroppable({
        id: 'due-columns',
        data: {
            "type": "due-calendar-column",
        }
    });

    //const { updateCoverage } = usePlanner();

    const { updateCoverage } = usePlanner();

    const { setNodeRef, isOver } = droppable;

    //const [numDueCols, setNumDueCols] = useState<number>(7);
    const [scrollLeft, setScrollLeft] = useState<number>(0);
    const [maxScrollLeft, setMaxScrollLeft] = useState<number>(Infinity);

    const { dueBlockWidth, startDate, loading } = usePlanner();
    const { planType } = useAuth();
    const { loadAuthModal } = useCommunication();


    const gap = 20;
    const columnWithGap = dueBlockWidth + gap; // Account for 20px gap
    const visibleColumnOffset = Math.floor(Math.abs(scrollLeft) / columnWithGap);
    const numCols = visibleColumnOffset + 7;

    // Check if user is on free/style plan (2-week limit)
    const isRestrictedUser = planType === "free" || planType === "style";

    const onScroll = (event: any) => {
        const target = event.target as HTMLDivElement;
        const currentScrollLeft = target.scrollLeft;

        setScrollLeft((prev: number) => {
            // If loading and trying to scroll beyond the boundary, prevent it
            if (loading && currentScrollLeft > maxScrollLeft) {
                target.scrollLeft = maxScrollLeft;
                return maxScrollLeft;
            }
            return currentScrollLeft;
        });
    };

    useEffect(() => {
        if (numCols % 7 !== 0) return;
        updateCoverage(startDate, addDays(startDate, numCols));
        setMaxScrollLeft(numCols * columnWithGap - gap);
    }, [numCols]);

    // Reset the scroll boundary when loading finishes
    useEffect(() => {
        if (!loading) {
            setMaxScrollLeft(Infinity); // Allow unlimited scrolling when not loading
        }
    }, [loading]);


    return (
        <div className="flex flex-col flex-1 min-h-0 min-w-0 relative overflow-hidden">
            <DueColumnsHeader />
            <div
                ref={setNodeRef}
                className="min-w-0 flex-1 ml-[20px] overflow-auto relative"
                onScroll={onScroll}
            >
                <div className="px-[10px] relative" style={{ width: `${numCols * columnWithGap - gap}px` }}>
                    <div className="absolute flex gap-[20px]" style={{ "left": `${visibleColumnOffset * columnWithGap}px` }}>
                        <DueColumns offset={visibleColumnOffset} isRestrictedUser={isRestrictedUser} />
                    </div>
                    <NonCalendarDragPreview />
                </div>
            </div>
            {/* Fixed centered modal that appears when scrolled to day 18 */}
            <AnimatePresence>
                {isRestrictedUser && scrollLeft >= 11 * columnWithGap && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="pointer-events-auto w-[600px] max-h-[460px] overflow-y-auto bg-white rounded-xl p-[15px] shadow-2xl">
                            <ClaimTrialContent highlight="assignments_due_view" />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export const DueView = () => {
    const { view } = usePlanner();

    if (view !== 'due') return null;

    return (
        <div className="flex flex-1 min-h-0">
            <DueColumnsContainer />
            <DueCalendar />
        </div>
    )
}

const formatDurationShort = (minutes: number) => {
    const hour = Math.floor(minutes / 60);
    const min = minutes % 60;
    return `${hour > 0 ? `${hour}h` : ''}${min > 0 ? ` ${min}m` : ''}`;
}

const formatDueShort = (date: string) => {
    const d = new Date(date);
    const diff = differenceInMinutes(d, new Date()); // minutes until due; negative if past
    const pastDue = diff < 0;
    const absMinutes = Math.abs(diff);

    const days = Math.floor(absMinutes / 1440);
    if (days > 0) {
        return `${days}d${pastDue ? ' ago' : ''}`;
    }

    const hours = Math.floor((absMinutes % 1440) / 60);
    if (hours > 0) {
        return `${hours}h${pastDue ? ' ago' : ''}`;
    }

    const minutes = absMinutes % 60;
    return `${minutes}m${pastDue ? ' ago' : ''}`;
}

const DueColumnHeader = memo(({ date, diff }: { date: Date, diff: number }) => {

    return (
        <div className="flex gap-[5px] items-center py-[10px] text-lg sticky top-0 left-0 bg-theme-0 z-5" style={{ "background": "linear-gradient(var(--bcbackground-0, #fff) 66%, transparent)" }}>
            <p className={`font-semibold ${diff === 0 ? "text-theme-link" : "text-theme-0"}`}>{diff === 0 ? "Due Today" : diff === 1 ? "Tomorrow" : format(date, "eee")}</p>
            <p className="text-theme-1">{format(date, "MMM d")}</p>
        </div>
    )
})

const DueCalendarColumns = () => {

    const { miniCalDate, settings, blockWidth, overrides, setScrollToHour, dueItems, planned, blockHeight } = usePlanner();
    const { schedule } = useCourses();
    //const planned = usePlanned();

    console.log("planned due calendar", planned);

    const dueItemLookup = useMemo(() => {
        const map = new Map<string, any>();
        dueItems.forEach((item: any) => {
            const id = (item?.current?.id ?? item?.original?.id)?.toString();
            if (id) map.set(id, item);
        });
        return map;
    }, [dueItems]);

    // Load calendar events for the current week view
    const calendarStartDate = useMemo(() => startOfDay(miniCalDate), [miniCalDate]);
    const calendarEndDate = useMemo(() => addDays(calendarStartDate, 7), [calendarStartDate]);
    const { events: calendarEvents } = useCalendarEvents(calendarStartDate, calendarEndDate);

    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    // Calculate columns and minHour separately
    const { columns, minHour } = useMemo(() => {

        let minHour = Infinity;
        const output = Array.from({ length: settings.assignments_minical_days }, (_, i) => {

            const date: Date = addDays(miniCalDate, i);

            const plannedItems: AssignmentPlanned[] = Object.keys(planned).reduce((acc: AssignmentPlanned[], key: string) => {
                const item = planned[key];
                console.log("item", item);
                if (!item.plannedAt) return acc;
                const itemDate = new Date(item.plannedAt);
                console.log("itemDate", itemDate);
                if (!areSameDay(itemDate, date)) return acc;
                if (itemDate.getHours() < minHour) minHour = itemDate.getHours();
                const fallback = dueItemLookup.get(key);
                const source = fallback?.current || fallback?.original || {};
                acc.push({
                    "id": key,
                    ...planned[key],
                    course: planned[key]?.course ?? source.course,
                    title: planned[key]?.title ?? source.title,
                    status: planned[key]?.status ?? source.status,
                    duration: planned[key]?.duration ?? source.duration ?? defaultTaskValues.duration,
                    ...(overrides[key]?.["title"] ? { "title": overrides[key]?.["title"] } : {}),
                    ...(overrides[key]?.["course"] ? { "course": overrides[key]?.["course"] } : {}),
                    ...(overrides[key]?.["status"] ? { "status": overrides[key]?.["status"] } : {}),
                });
                return acc;
            }, [] as AssignmentPlanned[]);

            const due = dueItems.filter((item: any) => {
                const itemDate = new Date(item.current.due);
                return areSameDay(itemDate, date);
            });

            const scheduleItems = schedule?.[date.getDay()] || [];

            // Filter calendar events (Google & Microsoft) for this date
            const externalCalendarItems = calendarEvents.filter(event => {
                return areSameDay(event.startDate, date) && !event.isAllDay;
            })


            // Update minHour based on external calendar events too
            externalCalendarItems.forEach(event => {
                const eventHour = new Date(event.plannedAt).getHours();
                if (eventHour < minHour) minHour = eventHour;
            });

            return (
                <CalendarColumn
                    start={date}
                    today={areSameDay(todayDate, date)}
                    items={plannedItems || []}
                    dueItems={due || []}
                    calendarEvents={externalCalendarItems || []}
                    scheduleItems={scheduleItems || []}
                    compactDuePopover={true}
                    showTimeIndicator={true}
                    key={date.toISOString()} // Use date as key to force re-render when date changes
                />
            )
        });

        return { columns: output, minHour };
    }, [miniCalDate, settings.assignments_minical_days, planned, schedule, calendarEvents, overrides, dueItemLookup]);

    // Update scroll position in an effect, not during render
    useEffect(() => {
        setScrollToHour(minHour);
    }, [minHour, setScrollToHour]);



    return (
        <div className="flex h-full" style={{ "width": `${settings.assignments_minical_days * blockWidth}px` }}>
            <div className="absolute flex h-full overflow-hidden" style={{ "maxHeight": `${(24 * blockHeight) + 52}px` }}>
                <CalendarRowLines />
                <CalendarColumnLines />
                {columns}
            </div>
        </div>
    );
}

const CalendarColumnLines = () => {

    const { settings, blockWidth } = usePlanner();
    const lines = Array.from({ length: settings.assignments_minical_days }, (_, i) => {
        return <div className="absolute w-[1px] h-full bg-theme-border" style={{ "left": ((i + 1) * blockWidth) + 'px' }} key={"row-line-" + i} />
    });

    return (
        <div className={`absolute top-0 left-0 w-full h-full z-[1]`}>
            {lines}
        </div>
    )
}


const MiniCalDate = memo(() => {

    const { miniCalDate } = usePlanner();

    return (
        <div className="flex items-center gap-[5px]">
            <p className="text-sm font-medium">{formatTime(miniCalDate, 'MMM d')}</p>
        </div>
    )
})

const CalendarSyncFooter = ({ isCollapsed }: { isCollapsed: boolean }) => {
    const { loadAuthModal } = useCommunication();
    const { planType } = useAuth();

    const isTrialUser = planType === "trial" || planType === "trialThenPro";
    const showUpgradeCTA = planType === "none" || planType === "free" || isTrialUser;
    const ctaText = isTrialUser ? "Explore Pro" : "Upgrade";
    const ctaTooltip = isTrialUser ? "Explore Pro plans" : "Upgrade to Learn+";

    const handleUpgradeClick = () => {
        loadAuthModal("upgrade");
    };

    if (!showUpgradeCTA) return null;

    return (
        <motion.div
            key="footer-bar"
            className={`border-t border-theme bg-theme-0 px-[10px] py-[6px] flex items-center relative z-[60] ${isCollapsed ? 'justify-center' : 'justify-end'}`}
            initial={false}
            animate={{ opacity: 1 }}
        >
            <Tooltip>
                <TooltipTrigger asChild>
                    <button onClick={handleUpgradeClick} className={`cursor-pointer hover:brightness-95 active:brightness-90 transition-all duration-200 bg-brand-gradient text-white rounded-lg shadow-lg shadow-brand-500/20 ${isCollapsed ? 'p-[6px]' : 'px-3 py-1.5 flex items-center justify-center gap-2 text-xs font-medium'}`}>
                        <Crown className={`${isCollapsed ? 'h-[16px] w-[16px]' : 'h-3.5 w-3.5'} fill-current`} />
                        {!isCollapsed && <span>{ctaText}</span>}
                    </button>
                </TooltipTrigger>
                <TooltipContent side={isCollapsed ? "right" : "top"}>
                    {ctaTooltip}
                </TooltipContent>
            </Tooltip>
        </motion.div>
    );
};

const DueCalendar = () => {
    const { scrollPos, setMiniCalDate, setScrollToHour, scrollToHour, scrollArea, miniCalDate, blockHeight, portal, settings: plannerSettings } = usePlanner();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [hasLoadedInitialState, setHasLoadedInitialState] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const lastToggleTime = useRef(0);
    const lastAutoScrollKey = useRef<string | null>(null);
    const localScrollRef = useRef<HTMLDivElement | null>(null);

    const [tooltipTop, setTooltipTop] = useState<number | null>(null);

    const { updateOptions, settings } = useCommunication();

    const isMiniCalToday = useMemo(() => {
        const today = new Date();
        return miniCalDate.getFullYear() === today.getFullYear() &&
            miniCalDate.getMonth() === today.getMonth() &&
            miniCalDate.getDate() === today.getDate();
    }, [miniCalDate]);

    const onMiniCalTodayClick = useCallback(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        setMiniCalDate(today);
    }, [setMiniCalDate]);

    useEffect(() => {
        if (settings.assignments_sidebar_collapsed !== undefined) {
            setIsCollapsed(settings.assignments_sidebar_collapsed);
        }
        setHasLoadedInitialState(true);
    }, []);

    const toggleCollapse = useCallback(() => {
        setIsCollapsed(prev => {
            const newValue = !prev;
            updateOptions({ assignments_sidebar_collapsed: newValue });
            return newValue;
        });
    }, []);

    const handleSettingsOpenChange = (newOpen: boolean) => {
        const now = Date.now();
        const timeSinceLastToggle = now - lastToggleTime.current;

        if (timeSinceLastToggle < 100) {
            return;
        }

        lastToggleTime.current = now;
        setSettingsOpen(newOpen);
    };

    const handleScroll = useCallback((event: any) => {
        const target = event.target as HTMLDivElement;
        scrollPos.current = { top: target.scrollTop, left: target.scrollLeft };
    }, [scrollPos]);

    const performAutoScroll = useCallback(() => {
        const node = localScrollRef.current;
        if (!node || !Number.isFinite(blockHeight) || blockHeight <= 0) return;
        
        const now = new Date();
        const desiredHour = (now.getHours() + (now.getMinutes() / 60)) - 2;
        const desiredTop = Math.max(0, desiredHour * blockHeight);
        
        const maxScroll = Math.max(0, node.scrollHeight - node.clientHeight);
        const nextTop = Math.min(maxScroll, desiredTop);
        node.scrollTop = nextTop;
        scrollPos.current = { top: nextTop, left: node.scrollLeft };
    }, [blockHeight, scrollPos]);

    useEffect(() => {
        if (!hasLoadedInitialState) return;
        if (!localScrollRef.current || !Number.isFinite(blockHeight) || blockHeight <= 0) return;
        
        const key = `day-${miniCalDate.toDateString()}`;
        if (lastAutoScrollKey.current === key) return;
        lastAutoScrollKey.current = key;

        const observer = new ResizeObserver(() => {
            performAutoScroll();
        });

        if (localScrollRef.current) {
            observer.observe(localScrollRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [blockHeight, miniCalDate, performAutoScroll, hasLoadedInitialState]);

    const [tooltipY, setTooltipY] = useState<number>(0);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipY(e.clientY - rect.top);
    };

    if (!hasLoadedInitialState) {
        return null;
    }


    return (
        <div
            className="flex flex-col min-h-0 border-l border-theme relative group/sidebar"
            style={{
                width: isCollapsed ? '50px' : 'auto',
                transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
        >
            <div
                className="absolute left-0 top-0 h-full w-[12px] cursor-pointer flex items-center justify-center z-[70] group/trigger"
                onClick={toggleCollapse}
                onMouseMove={handleMouseMove}
            >
                <div className="h-full w-[2px] bg-theme-border group-hover/trigger:bg-theme-link group-hover/trigger:w-[3px] transition-all duration-200 absolute left-0 top-0" />

                <div
                    className="absolute right-[100%] mr-2 bg-[#1F1F1F] text-white border border-white text-xs font-medium px-3 py-1.5 rounded-md shadow-xl opacity-0 group-hover/trigger:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-[100]"
                    style={{
                        top: `${tooltipY}px`,
                        transform: 'translateY(-50%)'
                    }}
                >
                    {isCollapsed ? "Expand Plan My Day" : "Collapse Plan My Day"}
                </div>
            </div>

            <div className={`flex-1 flex flex-col min-w-0 overflow-hidden ${isCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'} transition-opacity duration-200`}>
                <div className="flex items-center justify-between border-b border-theme p-[10px] h-[50px] overflow-hidden shrink-0 pl-[20px]">
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-[10px]">
                            <Popover open={settingsOpen} onOpenChange={handleSettingsOpenChange}>
                                <PopoverTrigger asChild>
                                    <div className="flex items-center gap-[5px] cursor-pointer">
                                        <p className="text-sm font-semibold whitespace-nowrap">Plan My Day</p>
                                        <Settings className="h-[16px] w-[16px]" />
                                    </div>
                                </PopoverTrigger>
                                <PopoverContent>
                                    <MiniCalSettings />
                                </PopoverContent>
                            </Popover>
                            {!isMiniCalToday && (
                                <button
                                    onClick={onMiniCalTodayClick}
                                    className="px-[8px] rounded-full py-[2px] text-xs text-theme-0 border border-theme cursor-pointer hover:bg-theme-1 transition-all duration-200"
                                >
                                    Now
                                </button>
                            )}
                        </div>
                        <div className="flex items-center gap-[5px]">
                            <div className="flex items-center gap-[5px]">
                                <button
                                    className="cursor-pointer hover:bg-theme-1 rounded-md p-[2px] transition-all duration-200"
                                    onClick={() => {
                                        const newDate = subDays(miniCalDate, plannerSettings.assignments_minical_days || 1);
                                        setMiniCalDate(newDate);
                                    }}
                                >
                                    <ChevronLeft className="h-[18px] w-[18px]" />
                                </button>
                                <MiniCalDate />
                                <button
                                    className="cursor-pointer hover:bg-theme-1 rounded-md p-[2px] transition-all duration-200"
                                    onClick={() => {
                                        const newDate = addDays(miniCalDate, plannerSettings.assignments_minical_days || 1);
                                        setMiniCalDate(newDate);
                                    }}
                                >
                                    <ChevronRight className="h-[18px] w-[18px]" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <AnimatePresence initial={false}>
                    {!isCollapsed && (
                        <div
                            ref={localScrollRef}
                            className="flex-1 min-h-0 relative overflow-auto"
                            style={{
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                            }}
                            onScroll={handleScroll}
                        >
                            <div className="grid grid-cols-[auto_1fr] relative w-full">
                                <div className="bg-theme-0 border-b border-theme sticky top-0 left-0 z-[11]"></div>
                                <DueCalendarHeaders />
                                <div className="col-span-2 sticky top-[40px] z-30">
                                    <MiniCalDueBar />
                                </div>
                                <div className="sticky top-0 left-0 bg-theme-0 z-10 border-r border-theme">
                                    <CalendarTimeIndicators />
                                </div>
                                <DueCalendarColumns />
                            </div>
                        </div>
                    )}
                </AnimatePresence>
                {/*<CalendarSyncFooter isCollapsed={isCollapsed} />*/}
            </div>
        </div>
    )
}

const MiniCalDueBar = () => {
    const { miniCalDate, dueItems } = usePlanner();
    const { courseData } = useCourses();
    const { loadSharedComponent } = useCommunication();

    const todayDue = useMemo(() => {
        return (dueItems || [])
            .filter((item: any) => {
                const dueDate = item?.current?.due ? new Date(item.current.due) : null;
                return dueDate ? isSameDay(dueDate, miniCalDate) : false;
            })
            .sort((a: any, b: any) => {
                const aDate = new Date(a.current.due).getTime();
                const bDate = new Date(b.current.due).getTime();
                return aDate - bDate;
            });
    }, [dueItems, miniCalDate]);

    const renderChip = (item: any, idx: number) => {
        const courseColor = courseData?.[item.current?.course || ""]?.color || "gray";
        const dueDate = item?.current?.due ? new Date(item.current.due) : null;

        let background = "rgba(0,0,0,0.04)";
        try {
            background = Color(courseColor).alpha(0.14).string();
        } catch (e) { /* noop */ }

        return (
            <button
                key={`mini-due-${item.current?.id || idx}`}
                className="flex items-center gap-[5px] rounded-md px-[7px] py-[5px] border border-theme bg-theme-0 text-theme-0 text-left w-full min-w-0 cursor-pointer hover:brightness-95 transition-all duration-150"
                style={{ borderColor: courseColor, background }}
                onClick={() => loadSharedComponent("AssignmentCreator", { assignment: item })}
            >
                <span className="h-[9px] w-[9px] rounded-full flex-shrink-0" style={{ background: courseColor }} />
                <span className="flex-1 min-w-0 font-semibold truncate text-[12.5px] leading-[14.5px]">{item.current?.title || "Due item"}</span>
                {dueDate && <span className="text-[10.5px] leading-[12.5px] text-theme-1 whitespace-nowrap">{formatTime(dueDate, "h:mmaaa")}</span>}
            </button>
        );
    };

    return (
        <div className="border-b border-theme bg-theme-0 px-[10px] py-[8px]">
            <div className="flex items-center gap-[8px] mb-[6px]">
                <p className="text-sm font-semibold">Due</p>
                <p className="text-xs text-theme-1">{todayDue.length} {todayDue.length === 1 ? "item" : "items"}</p>
            </div>
            <div className="flex flex-col gap-[6px]">
                {todayDue.length ? todayDue.map(renderChip) : (
                    <div className="rounded-md border border-dashed border-theme px-[8px] py-[6px] text-xs text-theme-1">
                        Nothing due today
                    </div>
                )}
            </div>
        </div>
    );
};

const DueCalendarHeaders = () => {
    const { miniCalDate, settings } = usePlanner();
    const today = new Date();

    const render = useMemo(() => {
        const ds: any[] = [];

        const days = Array.from({ length: settings.assignments_minical_days }, (_, i) => {
            const date = addDays(miniCalDate, i);
            ds.push(date.getDate());
            return <CalendarColumnHeader key={i} date={date} today={areSameDay(date, today)} />
        })

        return days;
    }, [miniCalDate, today]);

    return (
        <div className="sticky top-0 left-0 bg-theme-0 border-b border-theme h-[40px] bg-theme-0 z-20">
            <div className="flex absolute top-0 min-w-0 max-w-full h-full" >
                {render}
            </div>
        </div>
    )
}
